import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "error" }

import { ref, onMounted, reactive } from "vue";
import Uppy from "@uppy/core";
import Dashboard from "@uppy/dashboard";
import XHRUpload from "@uppy/xhr-upload";
import { useStore } from "vuex";

interface Props {
  uppyId: string;
  metadata: any;
  height: number;
  documentTypeId?: number;
  inline?: boolean;
  trigger?: string;
  uploadSuccess?: boolean;
  uploadComplete?: boolean;
  contactUs: any;
  modelValue?: any;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'UppyUploaderWrapper',
  props: {
    uppyId: {},
    metadata: {},
    height: { default: 400 },
    documentTypeId: {},
    inline: { type: Boolean, default: true },
    trigger: { default: ".file-upload" },
    uploadSuccess: { type: Boolean },
    uploadComplete: { type: Boolean },
    contactUs: {},
    modelValue: {}
  },
  emits: ["errorMessageUpdate", "uploadComplete", "uploadSuccess", "fileAdded"],
  setup(__props: any, { emit: __emit }) {

const store = useStore();

const props = __props;

const emit = __emit;

const errorMessageText = ref<string>("");
let uppy = reactive({} as any);

function instantiateUppy() {
  uppy = new Uppy({
    id: props?.uppyId,
    debug: false,
    autoProceed: false,
    allowMultipleUploads: false,
    infoTimeout: 6000, // 6 seconds
    locale: {
      strings: {
        exceedsSize:
          "%{file} is too large. Maximum supported file size is 100MB. Please reduce the file size and try again.",
        noInternetConnection: `Please check your internet connection and try again. If the issue continues and your internet connection is working, please try again later or contact our support team at ${props?.contactUs?.value?.tollFreeNumber}.`,
        failedToUpload: `An error occurred while processing your file. Please try again. If the issue continues, please contact our support team at ${props?.contactUs?.value?.tollFreeNumber}`,
      },
    },
    restrictions: {
      maxFileSize: 100000000, // 100MB
      maxNumberOfFiles: 1,
    },
    meta: props.metadata,
    onBeforeFileAdded: (currentFile, files) => {
      console.log("onBeforeFileAdded called", currentFile);
      console.log("onBeforeFileAdded props", props);

      const allowedFileTypes = ["doc", "docx", "pdf", "png", "jpg", "jpeg", "heic", "tif", "tiff"];

      const isValidFileType = allowedFileTypes.includes(currentFile.extension?.toLowerCase());
      const isValidFileSize = currentFile.size <= 100000000;
      if (!isValidFileType) {
        const formattedTypes = allowedFileTypes.map(type => type.toUpperCase()).join(", ");

        errorMessageText.value = `File format for ${currentFile.name} is not supported. Please upload a new file in one of these formats: ${formattedTypes}.`;
        emit("errorMessageUpdate", { errorMessage: errorMessageText.value });
        return false;
      }

      if (!isValidFileSize) {
        errorMessageText.value = `${currentFile.name} is too large. Maximum supported file size is 100MB. Please reduce the file size and try again.`;
        emit("errorMessageUpdate", { errorMessage: errorMessageText.value });
        return false;
      }

      errorMessageText.value = "";
      emit("errorMessageUpdate", { errorMessage: "" });
      return true;
    },
  })
    .use(Dashboard, {
      proudlyDisplayPoweredByUppy: false,
      showLinkToFileUploadResult: true,
      disableInformer: true, // custom error logging
      target: `#${props?.uppyId}`,
      trigger: props?.trigger || ".file-upload",
      inline: props?.inline || true,
      showSelectedFiles: true,
      height: props?.height || 400,
      disableThumbnailGenerator: false,
      hideUploadButton: false,
      disablePageScrollWhenModalOpen: false,
    })
    .use(XHRUpload, {
      endpoint: `${store.state.configuration?.docUploadUrl}/UploadDocument/`,
      formData: true,
      fieldName: "documentToUpload",
      allowedMetaFields: null,
      bundle: true,
      withCredentials: true,
      headers: {
        Authorization: `Bearer ${store.getters["oidcStore/oidcAccessToken"]}`,
        Accept: "*/*",
      },
      getResponseError(responseText, response) {
        console.log("getResponseError", { responseText, response });

        if (responseText?.toLowerCase()?.includes("password protected")) {
          errorMessageText.value = responseText;

          emit("errorMessageUpdate", { errorMessage: errorMessageText.value });
        }
        return new Error(JSON.parse(responseText).message);
      },
    });

  uppy.on("file-added", (file: any) => {
    emit("fileAdded");
    errorMessageText.value = "";
    emit("errorMessageUpdate", { errorMessage: errorMessageText.value });
    console.log("file added", file);
    console.log("file added documentTypeId", props.documentTypeId);

    
    if(file.meta.documentTypeId !== props.documentTypeId || file.meta.personId !== props.metadata?.personId)
    {
      uppy.setMeta(props.metadata);
    }
  });

  uppy.once("upload", (data: any) => {
    // Do something
    console.log("Uppy Upload", data);
  });

  uppy.on("error", (error: any) => {
    if (error) {
      errorMessageText.value = `An error occurred while processing your file. Please try again. If the issue continues, please contact our support team.`;

      emit("errorMessageUpdate", { errorMessage: errorMessageText.value });
    }
  });

  uppy.on("complete", async (result: any) => {
    console.log("Successful Files:", result.successful);
    console.log("Failed Files:", result.failed);

    emit("uploadComplete", result);

    if ((result.successful || []).length > 0) {
      emit("uploadSuccess");

      // reset form
      uppy.cancelAll();
    }

    if (result.failed.length > 0) {
      errorMessageText.value = `An error occurred while processing your file. Please try again. If the issue continues, please contact our support team.`;
      emit("errorMessageUpdate", errorMessageText.value);
    }
  });
}

onMounted(() => {
  instantiateUppy();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _renderSlot(_ctx.$slots, "default"),
    _createElementVNode("div", _hoisted_1, _toDisplayString(errorMessageText.value), 1)
  ], 64))
}
}

})